import * as bitcoin from 'bitcoinjs-lib';
import * as ecc from '@bitcoinerlab/secp256k1';
import { ECPairFactory } from 'ecpair';
import * as bip39 from 'bip39';
import BIP32Factory from 'bip32';
import { Buffer } from 'buffer';

if (typeof window !== 'undefined') {
    window.Buffer = Buffer;
}

const ECPair = ECPairFactory(ecc);
const bip32 = BIP32Factory(ecc);
bitcoin.initEccLib(ecc);

// Interchained (ITC) Network configuration
const ITCNETWORK = {
    messagePrefix: '\x18Interchained Signed Message:\n',
    bech32: 'itc',
    bip32: {
        public: 0x0488b21e,
        private: 0x0488ade4,
    },
    pubKeyHash: 0x00,
    scriptHash: 0x05,
    wif: 0x80,
};

const network = ITCNETWORK;

export async function initWallet(mnemonic) {
    const seed = await bip39.mnemonicToSeed(mnemonic);
    const root = bip32.fromSeed(seed, network);

    // Derive multiple addresses: 10 external (receive) + 5 internal (change)
    const addresses = [];

    function deriveAddress(chainIndex, addrIndex) {
        const path = `m/84'/0'/0'/${chainIndex}/${addrIndex}`;
        const child = root.derivePath(path);
        const { address } = bitcoin.payments.p2wpkh({ pubkey: child.publicKey, network });
        const output = bitcoin.payments.p2wpkh({ pubkey: child.publicKey, network }).output;
        const hash = bitcoin.crypto.sha256(output);
        const scriptHash = Buffer.from(hash).reverse().toString('hex');

        return {
            address,
            scriptHash,
            path,
            chain: chainIndex === 0 ? 'external' : 'change',
            index: addrIndex,
            wif: child.toWIF(),
            balance: 0,
            utxos: []
        };
    }

    // External addresses (receive)
    for (let i = 0; i < 10; i++) {
        addresses.push(deriveAddress(0, i));
    }
    // Change addresses
    for (let i = 0; i < 5; i++) {
        addresses.push(deriveAddress(1, i));
    }

    // Primary address is the first external
    const primary = addresses[0];

    return {
        mnemonic,
        address: primary.address,
        scriptHash: primary.scriptHash,
        keyPairWIF: primary.wif,
        addresses, // all derived addresses
        root // keep root for future derivation
    };
}

// Get key pair for a specific address entry
export function getKeyPairForAddress(addrEntry) {
    return ECPair.fromWIF(addrEntry.wif, network);
}

export async function createTransaction(walletData, utxos, toAddress, amountSats, feeRateSats) {
    let selectedUtxos = [];
    let accumulatedSats = 0;

    utxos.sort((a, b) => b.value - a.value);

    let estimatedFee = Math.ceil(150 * feeRateSats);
    let targetAmount = amountSats + estimatedFee;

    for (let u of utxos) {
        selectedUtxos.push(u);
        accumulatedSats += u.value;

        let vSizeEst = (selectedUtxos.length * 68) + (2 * 31) + 10;
        estimatedFee = Math.ceil(vSizeEst * feeRateSats);
        // Enforce minimum relay fee (at least 141 sats for ITC/BTC)
        if (estimatedFee < 141) estimatedFee = 141;
        targetAmount = amountSats + estimatedFee;

        if (accumulatedSats >= targetAmount) {
            break;
        }
    }

    if (accumulatedSats < targetAmount) {
        throw new Error(`Insufficient funds: Need ${targetAmount} sats (incl. fee), but only have ${accumulatedSats} sats combined across UTXOs.`);
    }

    const psbt = new bitcoin.Psbt({ network });

    for (let u of selectedUtxos) {
        // Resolve keyPair dynamically for this specific UTXO
        const keyPair = ECPair.fromWIF(u.wif, network);
        const p2wpkh = bitcoin.payments.p2wpkh({ pubkey: keyPair.publicKey, network });

        psbt.addInput({
            hash: u.tx_hash,
            index: u.tx_pos,
            witnessUtxo: {
                script: Uint8Array.from(p2wpkh.output),
                value: BigInt(u.value),
            }
        });
    }

    psbt.addOutput({
        address: toAddress,
        value: BigInt(amountSats),
    });

    const changeSats = accumulatedSats - targetAmount;
    if (changeSats > 546) {
        // Send change to the first *change* address (internal index 0)
        // walletData.addresses contains 10 external (0-9) and 5 internal (10-14)
        // Internal index 0 is at array index 10.
        const changeAddr = walletData.addresses[10].address;
        psbt.addOutput({
            address: changeAddr,
            value: BigInt(changeSats),
        });
    }

    // Sign each input with its respective key
    selectedUtxos.forEach((u, index) => {
        const keyPair = ECPair.fromWIF(u.wif, network);
        psbt.signInput(index, keyPair);
    });

    psbt.finalizeAllInputs();

    const tx = psbt.extractTransaction();
    const hex = tx.toHex();

    return {
        txHex: hex,
        feeSats: estimatedFee,
        vSize: tx.virtualSize()
    };
}
